import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dropdownAPI } from '../services/api';

// Natural sort comparator for alphanumeric strings (e.g., "Grower 2" before "Grower 19")
const naturalSort = (a, b) => {
  const regex = /(\d+)|(\D+)/g;
  const aParts = String(a).match(regex) || [];
  const bParts = String(b).match(regex) || [];

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = aParts[i] || '';
    const bPart = bParts[i] || '';

    // Check if both parts are numbers
    const aNum = parseInt(aPart, 10);
    const bNum = parseInt(bPart, 10);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      // Both are numbers, compare numerically
      if (aNum !== bNum) return aNum - bNum;
    } else {
      // At least one is text, compare alphabetically (case-insensitive)
      const comparison = aPart.toLowerCase().localeCompare(bPart.toLowerCase());
      if (comparison !== 0) return comparison;
    }
  }

  return 0;
};

const DropdownManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dropdownItems, setDropdownItems] = useState({});
  const [selectedListType, setSelectedListType] = useState('produce_type');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    listType: '',
    listValue: '',
    sortOrder: '',
  });

  const listTypes = [
    { value: 'produce_type', label: 'Produce Types' },
    { value: 'grower', label: 'Growers' },
    { value: 'class', label: 'Classes' },
    { value: 'unit', label: 'Units' },
    { value: 'size', label: 'Sizes' },
    { value: 'colour', label: 'Colours' },
  ];

  useEffect(() => {
    fetchAllDropdownItems();
  }, []);

  const fetchAllDropdownItems = async () => {
    try {
      setLoading(true);
      const allItems = {};

      // Fetch detailed items for each list type
      for (const { value } of listTypes) {
        const data = await dropdownAPI.getByType(value, true);
        // Sort by list_value (name) using natural sort (alphanumeric)
        allItems[value] = (data.items || []).sort((a, b) =>
          naturalSort(a.list_value, b.list_value)
        );
      }

      setDropdownItems(allItems);
      setError('');
    } catch (err) {
      console.error('Failed to fetch dropdowns:', err);
      setError('Failed to load dropdown lists');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await dropdownAPI.create(
        formData.listType,
        formData.listValue,
        formData.sortOrder ? parseInt(formData.sortOrder) : undefined
      );
      setSuccess('Value added successfully');
      setShowAddModal(false);
      setFormData({ listType: '', listValue: '', sortOrder: '' });
      setTimeout(() => setSuccess(''), 3000);
      fetchAllDropdownItems();
    } catch (err) {
      console.error('Add error:', err);
      setError(err.response?.data?.error || 'Failed to add value');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await dropdownAPI.update(
        editingItem.id,
        formData.listValue,
        formData.sortOrder ? parseInt(formData.sortOrder) : undefined
      );
      setSuccess('Value updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      setFormData({ listType: '', listValue: '', sortOrder: '' });
      setTimeout(() => setSuccess(''), 3000);
      fetchAllDropdownItems();
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Failed to update value');
    }
  };

  const handleDelete = async (id, listValue) => {
    if (!window.confirm(`Are you sure you want to delete "${listValue}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await dropdownAPI.delete(id);
      setSuccess('Value deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchAllDropdownItems();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Failed to delete value');
    }
  };

  const openAddModal = () => {
    setFormData({ listType: selectedListType, listValue: '', sortOrder: '' });
    setError('');
    setShowAddModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      listType: item.list_type,
      listValue: item.list_value,
      sortOrder: item.sort_order,
    });
    setError('');
    setShowEditModal(true);
  };

  const currentList = dropdownItems[selectedListType] || [];
  const currentListLabel = listTypes.find(t => t.value === selectedListType)?.label || '';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/pallets')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Pallet Sheets
              </button>
              <button
                onClick={() => navigate('/reports')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Reports
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#10b981',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Admin
                </button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h2 style={{ marginBottom: '30px' }}>Manage Dropdown Lists</h2>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #10b981',
            color: '#065f46',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {success}
          </div>
        )}

        {/* List Type Selector */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                Select List to Manage
              </label>
              <select
                value={selectedListType}
                onChange={(e) => setSelectedListType(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  backgroundColor: 'white'
                }}
              >
                {listTypes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={openAddModal}
              style={{
                padding: '12px 24px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginTop: '24px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
            >
              + Add New Value
            </button>
          </div>
        </div>

        {/* Selected List Items */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#374151' }}>
            {currentListLabel} ({currentList.length} items)
          </h3>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#6b7280' }}>Loading dropdown lists...</p>
            </div>
          ) : currentList.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '12px'
            }}>
              {currentList.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                      {item.list_value}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                    <button
                      onClick={() => openEditModal(item)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.list_value)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '40px' }}>
              No values added yet. Click "Add New Value" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Add New Value</h3>
            <form onSubmit={handleAdd}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                  List Type
                </label>
                <input
                  type="text"
                  disabled
                  value={listTypes.find(t => t.value === formData.listType)?.label || formData.listType}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                  Value *
                </label>
                <input
                  type="text"
                  required
                  value={formData.listValue}
                  onChange={(e) => setFormData({ ...formData, listValue: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  Items are automatically sorted alphabetically
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ listType: '', listValue: '', sortOrder: '' });
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Add Value
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Value</h3>
            <form onSubmit={handleEdit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                  List Type
                </label>
                <input
                  type="text"
                  disabled
                  value={listTypes.find(t => t.value === formData.listType)?.label || formData.listType}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                  Value *
                </label>
                <input
                  type="text"
                  required
                  value={formData.listValue}
                  onChange={(e) => setFormData({ ...formData, listValue: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  Items are automatically sorted alphabetically
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                    setFormData({ listType: '', listValue: '', sortOrder: '' });
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Update Value
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownManagement;
